import * as React from "react";
import * as ToastPrimitives from "@radix-ui/react-toast";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { Cross2Icon } from "@radix-ui/react-icons";

const ToastProvider = ToastPrimitives.Provider;

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      "testify-fixed testify-top-0 testify-z-[100] testify-flex testify-max-h-screen testify-w-full testify-flex-col-reverse testify-p-4 sm:testify-bottom-0 sm:testify-right-0 sm:testify-top-auto sm:testify-flex-col md:testify-max-w-[420px]",
      className,
    )}
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

const toastVariants = cva(
  "testify-group testify-pointer-events-auto testify-relative testify-flex testify-w-full testify-items-center testify-justify-between testify-space-x-2 testify-overflow-hidden testify-rounded-md testify-border testify-p-4 testify-pr-6 testify-shadow-lg testify-transition-all data-[swipe=cancel]:testify-translate-x-0 data-[swipe=end]:testify-translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:testify-translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:testify-transition-none data-[state=open]:testify-animate-in data-[state=closed]:testify-animate-out data-[swipe=end]:testify-animate-out data-[state=closed]:testify-fade-out-80 data-[state=closed]:testify-slide-out-to-right-full data-[state=open]:testify-slide-in-from-top-full data-[state=open]:sm:testify-slide-in-from-bottom-full",
  {
    variants: {
      variant: {
        default: "testify-border testify-bg-background testify-text-foreground",
        destructive:
          "testify-destructive testify-group testify-border-destructive testify-bg-destructive testify-text-destructive-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> &
    VariantProps<typeof toastVariants>
>(({ className, variant, ...props }, ref) => {
  return (
    <ToastPrimitives.Root
      ref={ref}
      className={cn(toastVariants({ variant }), className)}
      {...props}
    />
  );
});
Toast.displayName = ToastPrimitives.Root.displayName;

const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      "testify-inline-flex testify-h-8 testify-shrink-0 testify-items-center testify-justify-center testify-rounded-md testify-border testify-bg-transparent testify-px-3 testify-text-sm testify-font-medium testify-transition-colors hover:testify-bg-secondary focus:testify-outline-none focus:testify-ring-1 focus:testify-ring-ring disabled:testify-pointer-events-none disabled:testify-opacity-50 group-[.destructive]:testify-border-muted/40 group-[.destructive]:hover:testify-border-destructive/30 group-[.destructive]:hover:testify-bg-destructive group-[.destructive]:hover:testify-text-destructive-foreground group-[.destructive]:focus:testify-ring-destructive",
      className,
    )}
    {...props}
  />
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      "testify-absolute testify-right-1 testify-top-1 testify-rounded-md testify-p-1 testify-text-foreground/50 testify-opacity-0 testify-transition-opacity hover:testify-text-foreground focus:testify-opacity-100 focus:testify-outline-none focus:testify-ring-1 group-hover:testify-opacity-100 group-[.destructive]:testify-text-red-300 group-[.destructive]:hover:testify-text-red-50 group-[.destructive]:focus:testify-ring-red-400 group-[.destructive]:focus:testify-ring-offset-red-600",
      className,
    )}
    toast-close=""
    {...props}
  >
    <Cross2Icon className="testify-h-4 testify-w-4" />
  </ToastPrimitives.Close>
));
ToastClose.displayName = ToastPrimitives.Close.displayName;

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cn(
      "testify-text-sm testify-font-semibold [&+div]:testify-text-xs",
      className,
    )}
    {...props}
  />
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn("testify-text-sm testify-opacity-90", className)}
    {...props}
  />
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>;

type ToastActionElement = React.ReactElement<typeof ToastAction>;

export {
  type ToastProps,
  type ToastActionElement,
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
};
